import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowDown } from 'lucide-react';
import Navigation from './Navigation';
import { useLanguage } from '../i18n/LanguageContext';
import { trackEvent } from '../analytics';
import { SHOW_CONFIGURATOR_LINKS } from '../config/features';

const Hero = () => {
  const { t, path } = useLanguage();
  const sectionStyle = {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden'
  };

  const backgroundStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(to right,rgba(87, 92, 93, 0.5) 0%, #000000 100%)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    zIndex: 1
  };

  const overlayStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    zIndex: 2
  };

  const contentStyle = {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    zIndex: 10,
    padding: '0 1.5rem'
  };

  const textCenterStyle = {
    textAlign: 'center',
    maxWidth: '64rem',
    margin: '0 auto'
  };

  const logoContainerStyle = {
    marginBottom: '2rem',
    display: 'flex',
    justifyContent: 'center'
  };


  const subtitleStyle = {
    fontSize: '1.5rem',
    color: '#d1d5db',
    marginBottom: '1rem',
    fontWeight: '300'
  };

  const descriptionStyle = {
    fontSize: '1.125rem',
    color: '#9ca3af',
    marginBottom: '2rem',
    maxWidth: '32rem',
    margin: '0 auto 2rem auto',
    lineHeight: '1.6'
  };

  const buttonContainerStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: '3rem'
  };

  const primaryButtonStyle = {
    backgroundColor: '#dfa926',
    color: 'black',
    fontWeight: '600',
    padding: '1rem 2rem',
    borderRadius: '0.5rem',
    textDecoration: 'none',
    transition: 'all 0.3s',
    border: 'none',
    cursor: 'pointer',
    display: 'inline-block'
  };

  const secondaryButtonStyle = {
    border: '2px solid #dfa926',
    color: '#dfa926',
    backgroundColor: 'transparent',
    fontWeight: '600',
    padding: '1rem 2rem',
    borderRadius: '0.5rem',
    textDecoration: 'none',
    transition: 'all 0.3s',
    cursor: 'pointer',
    display: 'inline-block'
  };

  const arrowStyle = {
    animation: 'bounce 2s infinite',
    color: '#dfa926'
  };

  return (
    <section style={sectionStyle}>
      <Navigation />
      
      {/* Background with overlay */}
      <div style={backgroundStyle}></div>
      <div style={overlayStyle}></div>
      
      <div style={contentStyle}>
        <div style={textCenterStyle}>
          <div style={logoContainerStyle}>
            <img 
              src="/logo.png" 
              alt="Hestia Doors Logo"
              fetchpriority="high"
              style={{ height: '8rem', width: 'auto', marginBottom: '1rem' }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentNode.innerHTML = '<div style="font-size: 4rem; font-weight: bold; color: #dfa926; margin-bottom: 1rem;">HESTIA DOORS</div>';
              }}
            />
          </div>
          
          <p style={{ color: '#dfa926', fontSize: '0.95rem', fontWeight: '500', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: '1rem' }}>
            {t('hero.tagline')}
          </p>

          <h1 style={subtitleStyle}>
            {t('hero.h1')}
          </h1>

          <p style={descriptionStyle}>
            {t('hero.desc')}
          </p>

          <div style={buttonContainerStyle} className="d-sm-flex flex-sm-row">
            <Link to={path('/products')} onClick={() => trackEvent('explore_doors_click', { location: 'hero' })}>
              <div 
                style={primaryButtonStyle}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#c8941f';
                  e.target.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#dfa926';
                  e.target.style.transform = 'scale(1)';
                }}
              >
                {t('hero.products')}
              </div>
            </Link>
            <Link
              to={path(SHOW_CONFIGURATOR_LINKS ? '/configurator' : '/contact')}
              onClick={() => trackEvent(SHOW_CONFIGURATOR_LINKS ? 'configurator_click' : 'request_quote_click', { location: 'hero' })}
            >
              <div 
                style={secondaryButtonStyle}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#dfa926';
                  e.target.style.color = 'black';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#dfa926';
                }}
              >
                {SHOW_CONFIGURATOR_LINKS ? t('nav.configurator') : t('hero.quote')}
              </div>
            </Link>
          </div>
          
          <div style={arrowStyle}>
            <ArrowDown size={32} style={{ margin: '0 auto', display: 'block' }} />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes bounce {
          0%, 20%, 53%, 80%, 100% {
            transform: translate3d(0,0,0);
          }
          40%, 43% {
            transform: translate3d(0,-30px,0);
          }
          70% {
            transform: translate3d(0,-15px,0);
          }
          90% {
            transform: translate3d(0,-4px,0);
          }
        }
      `}</style>
    </section>
  );
};

export default Hero;
